import FeatureGates from '@atlaskit/feature-gate-js-client';
import { UFOv2 } from '@atlassian/post-office-frontend-performance-tracking';
import { memo } from 'react';

import type { PerformanceTrackingProviderProps, PerformanceTrackingSegmentProps } from './types';
import { TrackMount } from '../../../track-mount';
import { usePerformanceTracking } from '../../hooks/usePerformanceTracking';

export const PerformanceTracking = memo<PerformanceTrackingProviderProps>(
	({ children, ...props }) => {
		const isUFOEnabled = FeatureGates.checkGate('enable_ufo_tracking_in_post_office');

		const { isLoading, onMount } = usePerformanceTracking(props);

		return (
			<>
				{isUFOEnabled && isLoading && <UFOv2.UFOLoadHold name={UFOv2.HoldNames.PLACEMENT} />}
				<TrackMount onMount={onMount}>{children}</TrackMount>
			</>
		);
	},
);

/**
 * This component fires two UFOv2 segment events:
 * - `post-office-placement`: to track overall placement
 * - `post-office-placement.{some-placement-id}`: to track individual placement performance
 *
 * When using this component, ensure that the parent component is only fired once. Otherwise,
 * this component will fire these events every time the parent re-renders.
 */
export const PerformanceTrackingSegment = memo<PerformanceTrackingSegmentProps>(
	({ placementId, children }) => {
		const isEnabled = FeatureGates.checkGate('post_office_update_ufov2_placements');

		/**
		 * If feature gate is not enabled, then we just return the children without the
		 * UFO segments attached.
		 */
		if (!isEnabled) return children;

		return (
			<UFOv2.UFOSegment name={UFOv2.SegmentNames.PLACEMENT}>
				<UFOv2.UFOSegment name={`${UFOv2.SegmentNames.PLACEMENT}.${placementId}`}>
					{children}
				</UFOv2.UFOSegment>
			</UFOv2.UFOSegment>
		);
	},
);
